import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';

const TooltipStyle = styled.span`
  position: absolute;
  background: #26292c;
  color: #fff;
  font-size: 12px;
  line-height: 1.35;
  padding: 2px 8px;
  border-radius: 6px;
  white-space: normal;
  max-width: 320px;
  overflow-wrap: anywhere;
  z-index: 99999;
  pointer-events: ${({ $interactive }) => ($interactive ? 'auto' : 'none')};
  user-select: ${({ $interactive }) => ($interactive ? 'text' : 'none')};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 0.2s ease;

  @media (max-width: 360px) {
    max-width: calc(100vw - 16px);
  }

	ul {
		margin: 0;
		padding: 0;
		list-style: none;
	}

	ul,
	li {
		color: #fff;
	}
`;

export const TooltipWrapper = styled.div`
  display: inline-block;
  position: relative;
`;

export const clearTooltipWindowListeners = (
	listenersRef,
	windowObject = typeof window === 'undefined' ? null : window,
) => {
	if (!windowObject) {
		return;
	}

	if (listenersRef.current.resize) {
		windowObject.removeEventListener('resize', listenersRef.current.resize);
	}

	if (listenersRef.current.scroll) {
		windowObject.removeEventListener('scroll', listenersRef.current.scroll, true);
	}

	listenersRef.current = {
		resize: null,
		scroll: null,
	};
};

export const canUpdateTooltipPosition = (
	visible,
	wrapperElement,
	tooltipElement,
	windowObject = typeof window === 'undefined' ? null : window,
) => (
	Boolean(visible && wrapperElement && tooltipElement && windowObject)
);

export const createTooltipWindowChangeHandler = (updatePosition) => () => {
	if (typeof updatePosition === 'function') {
		updatePosition();
	}
};

export const subscribeTooltipWindowListeners = (
	listenersRef,
	updatePosition,
	windowObject = typeof window === 'undefined' ? null : window,
) => {
	if (!windowObject) {
		return;
	}

	const handleWindowChange = createTooltipWindowChangeHandler(updatePosition);

	listenersRef.current = {
		resize: handleWindowChange,
		scroll: handleWindowChange,
	};

	windowObject.addEventListener('resize', handleWindowChange);
	windowObject.addEventListener('scroll', handleWindowChange, true);
};

export const resolveTooltipCoordinates = ({
	margin = 8,
	position,
	rect,
	tipRect,
	viewportHeight,
	viewportWidth,
}) => {
	let top = 0;
	let left = 0;

	switch (position) {
	case 'top':
		top = rect.top - tipRect.height - margin;
		left = rect.left + rect.width / 2 - tipRect.width / 2;
		break;
	case 'bottom':
		top = rect.bottom + margin;
		left = rect.left + rect.width / 2 - tipRect.width / 2;
		break;
	case 'left':
		top = rect.top + rect.height / 2 - tipRect.height / 2;
		left = rect.left - tipRect.width - margin;
		break;
	case 'right':
		top = rect.top + rect.height / 2 - tipRect.height / 2;
		left = rect.right + margin;
		break;
	default:
		break;
	}

	const maxLeft = Math.max(margin, viewportWidth - tipRect.width - margin);
	const maxTop = Math.max(margin, viewportHeight - tipRect.height - margin);

	const clampedLeft = Math.min(Math.max(left, margin), maxLeft);
	const clampedTop = Math.min(Math.max(top, margin), maxTop);

	return {
		top: clampedTop,
		left: clampedLeft,
	};
};

const Tooltip = ({
	tooltip,
	className,
	children,
	position = 'top',
	interactive = false,
}) => {
	const [visible, setVisible] = useState(false);
	const portalContainer = typeof document !== 'undefined' ? document.body : null;

	const [coords, setCoords] = useState({
		top: 0,
		left: 0,
	});

	const wrapperRef = useRef(null);
	const tooltipRef = useRef(null);

	const listenersRef = useRef({
		resize: null,
		scroll: null,
	});
	const hideTimeoutRef = useRef(null);

	const clearHideTimeout = useCallback(() => {
		if (!hideTimeoutRef.current) {
			return;
		}

		clearTimeout(hideTimeoutRef.current);
		hideTimeoutRef.current = null;
	}, []);

	const showTooltip = useCallback(() => {
		clearHideTimeout();
		setVisible(true);
	}, [clearHideTimeout]);

	const hideTooltip = useCallback(() => {
		clearHideTimeout();

		if (!interactive) {
			setVisible(false);
			return;
		}

		hideTimeoutRef.current = setTimeout(() => {
			setVisible(false);
			hideTimeoutRef.current = null;
		}, 300);
	}, [clearHideTimeout, interactive]);

	const keepTooltipVisible = useCallback(() => {
		if (!interactive) {
			return;
		}

		clearHideTimeout();
		setVisible(true);
	}, [clearHideTimeout, interactive]);

	const updatePosition = useCallback(() => {
		if (!canUpdateTooltipPosition(visible, wrapperRef.current, tooltipRef.current)) {
			return;
		}

		const margin = 8;
		const rect = wrapperRef.current.getBoundingClientRect();
		const tipRect = tooltipRef.current.getBoundingClientRect();

		const viewportWidth = window.innerWidth || 0;
		const viewportHeight = window.innerHeight || 0;

		setCoords(resolveTooltipCoordinates({
			margin,
			position,
			rect,
			tipRect,
			viewportWidth,
			viewportHeight,
		}));
	}, [position, visible]);

	useEffect(() => {
		if (!visible) {
			return undefined;
		}

		subscribeTooltipWindowListeners(listenersRef, updatePosition);

		return () => {
			clearTooltipWindowListeners(listenersRef);
		};
	}, [updatePosition, visible]);

	useEffect(() => {
		if (!visible) {
			return;
		}

		updatePosition();
	}, [updatePosition, visible, position, tooltip]);

	useEffect(() => () => clearHideTimeout(), [clearHideTimeout]);

	return (
		<>
			<TooltipWrapper
				ref={wrapperRef}
				className={className}
				onMouseEnter={showTooltip}
				onMouseLeave={hideTooltip}
			>
				{children}
			</TooltipWrapper>

			{portalContainer
				&& ReactDOM.createPortal(
					<TooltipStyle
						ref={tooltipRef}
						$visible={visible}
						$interactive={interactive}
						onMouseEnter={interactive ? keepTooltipVisible : undefined}
						onMouseLeave={interactive ? hideTooltip : undefined}
						style={{
							top: `${coords.top}px`,
							left: `${coords.left}px`,
							position: 'fixed',
						}}
					>
						{tooltip}
					</TooltipStyle>,
					portalContainer,
				)}
		</>
	);
};

export default Tooltip;
